import { useContext, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getMyTickets } from '../api/ticketApi';
import AccountContext from '../context/AccountContext';
import { BaseTicket, DrawingTicket, TicketResource, TicketTypeResponse } from '../utils/types';

/**
 * Helper method for converting a TicketResource to a TicketTypeResponse.
 * TicketResource: is used as a path param in the request (e.g. /me/{TicketResource}/tickets)
 * TicketTypeResponse: is the "details.type" value returned in the JSON
 * @param ticketType TicketResource
 * @return TicketTypeResponse
 */
const drawingRequestToResponseType = (ticketType: TicketResource): TicketTypeResponse => {
    switch (ticketType) {
        case TicketResource.DRAWINGS:
            return TicketTypeResponse.DRAWING_TICKET;
        case TicketResource.EVENT_GATES:
            return TicketTypeResponse.EVENT_GATE_TICKET;
        case TicketResource.TIME_SLOTS:
            return TicketTypeResponse.TIME_SLOT_TICKET;
        default:
            throw new Error();
    }
};

export type MyTicketsByTypeMap = Map<TicketTypeResponse, Array<DrawingTicket | BaseTicket>>;

export const useMyTickets = () => {
    const { value: accountInfo } = useContext(AccountContext);

    // My Activities states
    const [myTickets, setMyTickets] = useState<MyTicketsByTypeMap>(new Map());
    const [loadingMyTickets, setLoadingMyTickets] = useState(false);
    const userIdRef = useRef(accountInfo.userId);

    // Setup helper function
    const refreshMyTickets = useCallback(async () => {
        setLoadingMyTickets(true);
        setMyTickets(new Map<TicketTypeResponse, Array<DrawingTicket | BaseTicket>>());

        const responseMap = new Map();
        // Call getMyTickets for each ticket type and store the response in myTickets mapped by MyTicketTypeResponse key
        // The promise all allows us to retrieve tickets for each ticket type in parallel
        // The map operation groups the response data by ticket type
        await Promise.all(
            Object.values(TicketResource).map(async (ticketType: TicketResource) => {
                // Use TicketResource
                const updatedTicketsForType = await getMyTickets(ticketType, accountInfo.token);
                if (updatedTicketsForType) {
                    // Store resulting tickets with a TicketTypeResponse value
                    responseMap.set(
                        drawingRequestToResponseType(ticketType),
                        updatedTicketsForType,
                    );
                }
                return updatedTicketsForType;
            }),
        );
        setMyTickets(responseMap);
        setLoadingMyTickets(false);
    }, [accountInfo.token]);

    useEffect(() => {
        if (accountInfo.token && myTickets.size === 0) {
            setLoadingMyTickets(true);
            refreshMyTickets();
        } else if (accountInfo.userId !== userIdRef.current) {
            setMyTickets(new Map());
            userIdRef.current = accountInfo.userId;
        }
    }, [accountInfo.token, accountInfo.userId, myTickets.size, refreshMyTickets]);

    return useMemo(
        () => ({
            value: myTickets,
            loading: loadingMyTickets,
            refresh: refreshMyTickets,
        }),
        [loadingMyTickets, myTickets, refreshMyTickets],
    );
};

export type UseMyTickets = ReturnType<typeof useMyTickets>;
